import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { Client } from 'src/providers/client/client';
import { Invoices } from 'src/providers/invoices/invoices';
import { Vendors } from 'src/providers/vendors/vendors';

import { Vendor } from 'src/models/vendor.model';
import { CreateInvoicePayload } from 'src/models/invoice.model';

interface LineItemForm {
  description: FormControl<string | null>;
  amount: FormControl<number | null>;
  address_id: FormControl<number | null>;
}

@Component({
  templateUrl: 'create-invoice.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CreateInvoicePage implements OnInit {
  private fb = inject(FormBuilder);
  private vendorsProvider = inject(Vendors);
  private invoices = inject(Invoices);
  private client = inject(Client);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);

  form = this.fb.group({
    vendor_id: this.fb.control<number | null>(null, Validators.required),
    send_email: this.fb.control<boolean>(false),
    items: this.fb.array<FormGroup<LineItemForm>>([], Validators.required),
  });

  vendors: Vendor[] = [];
  vendorItems: { value: number; viewValue: string }[] = [];
  addresses: any[] = [];
  addressItems: { value: number; viewValue: string }[] = [];
  submitted = false;
  errorMessage = '';
  loaded = false;
  isRightSideContent = false;
  preSelectedVendor: Vendor | null = null;

  boundSubmit = this.submit.bind(this);
  boundAddItem = this.addItem.bind(this);

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Create Invoice');
    }
    this.preSelectedVendor = this.navCtrl.getParam('vendor') ||
      (await this.storage.retrieve('dialog-params'))?.vendor || null;

    try {
      const [vendors, addresses] = await Promise.all([
        firstValueFrom(this.vendorsProvider.getVendors()),
        this.client.getAddresses(),
      ]);
      this.vendors = vendors;
      this.vendorItems = vendors.map(v => ({ value: v.id, viewValue: v.name }));
      this.addresses = addresses || [];
      this.addressItems = this.addresses.map(a => ({
        value: a.id,
        viewValue: a.address_name || a.address1,
      }));

      if (this.preSelectedVendor) {
        this.form.patchValue({ vendor_id: this.preSelectedVendor.id });
      }
      this.addItem();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load form';
    }
    this.loaded = true;
  }

  get items(): FormArray<FormGroup<LineItemForm>> {
    return this.form.controls.items;
  }

  buildItem(): FormGroup<LineItemForm> {
    return this.fb.group<LineItemForm>({
      description: this.fb.control<string | null>('', { validators: [Validators.required] }),
      amount: this.fb.control<number | null>(null, { validators: [Validators.required, Validators.min(0.01)] }),
      address_id: this.fb.control<number | null>(null, { validators: [Validators.required] }),
    });
  }

  addItem() {
    this.items.push(this.buildItem());
  }

  removeItem(index: number) {
    if (this.items.length > 1) {
      this.items.removeAt(index);
    }
  }

  async submit() {
    this.submitted = true;
    if (!this.form.valid || this.items.length === 0) {
      return;
    }
    try {
      const raw = this.form.getRawValue();
      const payload: CreateInvoicePayload = {
        seller_id: raw.vendor_id!,
        seller_type: 'Vendor',
        send_email: !!raw.send_email,
        invoice_items: this.items.controls.map(group => {
          const v = group.getRawValue();
          return {
            type: 'Item',
            data: {
              amount: Math.round((v.amount || 0) * 100),
              description: v.description || '',
            },
            address_id: v.address_id!,
          };
        }),
      };
      await this.invoices.createInvoice(payload);
      this.util.showSuccess('Invoice created');
      this.navCtrl.navigateBack('bills');
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to create invoice';
    }
  }
}
